/**
 * Render the "Personal Cycle Insight" card (7-Year Life Clock).
 *
 * Deterministic: takes the object produced by computeLifeClock() and paints
 * it into the given container. No AI, no async, no network.
 */

import { buildWaveGraph } from "./wave-graph.js";
import {
  describeCountdown,
  describeDayDistance,
  formatDate,
  WAVE_WINDOW_DAYS,
} from "./life-clock.js";
import { buildTurningPointReading } from "./reading.js";

function esc(value) {
  return String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * One reading section: heading, paragraphs, optional bullet list and an
 * optional graphic shown at the top-left of the heading (prose falls below).
 * @param {{heading: string, paragraphs?: string[], bullets?: string[], image?: {src: string, alt: string}}} section
 */
function renderReadingSection(section) {
  const paragraphs = (section.paragraphs || [])
    .map((text) => `\n    <p>${esc(text)}</p>`)
    .join("");
  const bullets =
    section.bullets && section.bullets.length
      ? `\n    <ul class="cycle-reading-card__list">${section.bullets
          .map((item) => `<li>${esc(item)}</li>`)
          .join("")}</ul>`
      : "";
  const heading = `<h4 class="cycle-reading-card__heading">${esc(section.heading)}</h4>`;
  const header =
    section.image && section.image.src
      ? `
    <div class="cycle-reading-card__section-header">
      <img class="cycle-reading-card__graphic" src="${esc(section.image.src)}" alt="${esc(section.image.alt)}" loading="lazy" decoding="async" />
      ${heading}
    </div>`
      : `
    ${heading}`;
  return `
  <section class="cycle-reading-card__section">${header}${paragraphs}${bullets}
  </section>`;
}

/**
 * @param {HTMLElement|null} container  Host element (e.g. #cycle-insight).
 * @param {object|null} clock           Result of computeLifeClock().
 */
export function renderCycleInsight(container, clock) {
  if (!container) return;

  if (!clock) {
    container.hidden = true;
    container.replaceChildren();
    return;
  }

  const {
    name,
    referenceDate,
    cycleStart,
    cycleEnd,
    cycleNumber,
    cycleStartAge,
    cycleEndAge,
    ageAtReference,
    next,
    activeWave,
    window: windowInfo,
    guidanceTitle,
    guidanceText,
    daysToNext,
    daysToActiveWave,
  } = clock;

  // The wave graph and the reading beneath it both centre on the turning point
  // the clock is presently following: the one being approached, or the one just
  // passed (through day +8, when the focus hands over to the next period).
  // The "Next Turning Point" box always looks forward.
  const waveTp = activeWave || next;
  const waveDays = daysToActiveWave;
  const nextTp = next || waveTp;

  const title = name ? `${name}'s 7-Year Life Clock` : "Your 7-Year Life Clock";
  const subtitle =
    `Reference date: ${formatDate(referenceDate)} &nbsp;•&nbsp; ` +
    `Cycle dates: ${formatDate(cycleStart)} – ${formatDate(cycleEnd)}`;

  const wave = buildWaveGraph(waveDays, {
    windowDays: WAVE_WINDOW_DAYS,
    peakLabel: formatDate(waveTp.date),
    dotLabel: "Today",
    centerDate: waveTp.date,
  });

  // Every 7-day marker on the wave is a challenge day in its own right.
  const challengeMarkerNote =
    "Each 7-day marker is a Challenge day that can forecast (within one day) a particularly challenging day.";

  const dotNote =
    (typeof waveDays === "number" && Math.abs(waveDays) <= WAVE_WINDOW_DAYS
      ? `Your blue dot sits ${esc(describeDayDistance(waveDays))}.`
      : `Today is outside the ±${WAVE_WINDOW_DAYS}-day window around this turning point, so the wave shows its shape without your marker. Your marker reappears once the date is within ${WAVE_WINDOW_DAYS} days of today.`) +
    ` ${esc(challengeMarkerNote)}`;

  // Substack-style reading for the turning point the wave graph is centred on,
  // so the reading and the blue graph marker advance together.
  const reading = buildTurningPointReading(clock);
  const readingHtml = reading
    ? `
<section class="cycle-reading-card" aria-label="Turning point reading">
  <p class="cycle-eyebrow">${esc(reading.eyebrow)}</p>
  <h3 class="cycle-reading-card__title">${esc(reading.heading)}</h3>
  <p class="cycle-reading-card__standfirst">${esc(reading.standfirst)}</p>
${reading.sections.map(renderReadingSection).join("")}
</section>`
    : "";

  container.innerHTML = `
<section class="cycle-dashboard" aria-live="polite">
  <header class="cycle-dashboard__header">
    <div class="cycle-dashboard__heading">
      <p class="cycle-eyebrow">Personal Cycle Insight</p>
      <h2 class="cycle-dashboard__title">${esc(title)}</h2>
      <p class="cycle-dashboard__subtitle">${subtitle}</p>
    </div>
    <span class="cycle-intensity-badge ${esc(windowInfo.intensity.className)}">${esc(windowInfo.intensity.label)}</span>
  </header>

  <div class="cycle-dashboard__grid">
    <article class="cycle-stat-card">
      <span class="cycle-stat-card__label">Current 7-Year Cycle</span>
      <strong>Cycle ${cycleNumber}: Age ${cycleStartAge}–${cycleEndAge}</strong>
      <small>Age at reference date: ${ageAtReference.toFixed(1)} years</small>
    </article>
    <article class="cycle-stat-card">
      <span class="cycle-stat-card__label">Next Turning Point</span>
      <strong>${formatDate(nextTp.date)}</strong>
      <small>${esc(describeCountdown(daysToNext))}</small>
    </article>
    <article class="cycle-stat-card cycle-stat-card--accent ${esc(nextTp.type.className)}">
      <span class="cycle-stat-card__label">Turning Point Type</span>
      <strong>${esc(nextTp.type.label)}</strong>
      <small>${esc(nextTp.type.meaning)}</small>
    </article>
  </div>

  <div class="cycle-wave-card">
    <div class="cycle-wave-card__head">
      <h3>Turning-point wave</h3>
      <div class="cycle-wave-card__copy">
        <div class="cycle-wave-card__col">
          <p>Each Turning Point date is part of 4 challenge and growth periods. These periods are related to one of the Signs of the Zodiac.</p>
          <p>The sign of the zodiac characterizes in some fundamental sense the types of experiences you may encounter at each Turning Point Period. Two weeks or so prior to the Turning Point date itself your life may grow more demanding on your attention and energy.</p>
        </div>
        <div class="cycle-wave-card__col">
          <p>A week or so after the Turning Point date you should see signs of a resolution until the next Turning Point Period brings another growth challenge.</p>
          <p>The crest is the exact turning point. The cyan flanks are where the turning-point period begins; the color climbs through green, yellow and orange to red at the peak.</p>
        </div>
      </div>
    </div>
    <div class="cycle-wave-graph">${wave}</div>
    <p class="cycle-wave-note">${dotNote}</p>
  </div>
  <div class="cycle-guidance-card">
    <h3>${esc(guidanceTitle)}</h3>
    <p>${esc(guidanceText)}</p>
  </div>
${readingHtml}
</section>`.trim();

  container.hidden = false;
}
